import { Data, Match, Option } from "effect"

export type ArrowKeyDirection = Data.TaggedEnum<{
  Previous: {}
  Next: {}
}>

export const ArrowKeyDirection = Data.taggedEnum<ArrowKeyDirection>()

export type ArrowKeyPress = Pick<
  KeyboardEvent,
  "key" | "altKey" | "ctrlKey" | "metaKey" | "shiftKey" | "defaultPrevented" | "target"
>

const SLIDE = "[data-role=carousel-slide]"

const MEDIA_PLAYER = "[data-media-player]"

const KEYBOARD_CONTROLS = [
  MEDIA_PLAYER,
  "input",
  "textarea",
  "select",
  "[contenteditable]:not([contenteditable=false])",
  "[role=slider]",
  "[role=radiogroup]",
  "[role=tablist]",
  "[role=menu]",
  "[role=menubar]",
  "[role=listbox]",
  "[role=combobox]",
  "[role=toolbar]"
].join(", ")

const isWithinKeyboardControl = (target: EventTarget | null) => {
  return target instanceof Element && target.closest(KEYBOARD_CONTROLS) !== null
}

const isInSlide = (element: Element, index: number) => {
  const slide = element.closest(SLIDE)
  if (!slide?.parentElement) return false
  return Array.from(slide.parentElement.children).indexOf(slide) === index
}

export namespace ArrowKeyNavigation {
  export const directionOf = (press: ArrowKeyPress): Option.Option<ArrowKeyDirection> => {
    if (press.defaultPrevented || press.altKey || press.ctrlKey || press.metaKey || press.shiftKey) {
      return Option.none()
    }
    if (isWithinKeyboardControl(press.target)) return Option.none()

    return Match.value(press.key).pipe(
      Match.when("ArrowLeft", () => {
        return Option.some(ArrowKeyDirection.Previous())
      }),
      Match.when("ArrowRight", () => {
        return Option.some(ArrowKeyDirection.Next())
      }),
      Match.orElse(() => {
        return Option.none()
      })
    )
  }

  // a swipe leaves focus in the previous slide's player, where it would keep the arrows seeking an off-screen video
  export const isFocusLeftBehind = (scope: Element, focused: Element | null, selectedSlide: number) => {
    const player = focused?.closest(MEDIA_PLAYER)
    if (!player) return false
    return scope.contains(player) && !isInSlide(player, selectedSlide)
  }
}
